import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const EnquiryCard = ({ enquiry, onViewMessages, onEditEnquiry, onCloseEnquiry }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (date) => {
    if (!date) return 'Flexible';
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatBudget = (amount, currency) => {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatter?.format(amount);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-primary text-primary-foreground',
      in_discussion: 'bg-warning text-warning-foreground',
      booked: 'bg-success text-success-foreground',
      closed: 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || colors?.open;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft hover:shadow-elevated transition-all duration-200">
      {/* Card Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-foreground">{enquiry?.destination}</h3>
              {enquiry?.unreadMessages > 0 && (
                <div className="w-2.5 h-2.5 bg-orange-500 rounded-full" title="Unread messages"></div>
              )}
            </div>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={14} />
                <span>{formatDate(enquiry?.startDate)} - {formatDate(enquiry?.endDate)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={14} />
                <span>{enquiry?.travelers} travelers</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="PoundSterling" size={14} />
                <span>{formatBudget(enquiry?.budget, enquiry?.currency)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </button>
        </div>
      </div>
      {/* Card Content */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{enquiry?.matchedAgents || 0}</div>
              <div className="text-xs text-muted-foreground">Matched Agents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent">
                {enquiry?.unreadMessages > 0 ? enquiry?.unreadMessages : 0}
              </div>
              <div className="text-xs text-muted-foreground">
                {enquiry?.unreadMessages > 0 ? 'New Messages' : (
                  enquiry?.lastMessageTime 
                    ? `Last: ${formatDateTime(enquiry?.lastMessageTime)}`
                    : 'No Messages'
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-success">
                {enquiry?.lowestQuote 
                  ? formatBudget(enquiry?.lowestQuote, enquiry?.quoteCurrency)
                  : '£0'}
              </div>
              <div className="text-xs text-muted-foreground">
                {enquiry?.lowestQuote ? 'Lowest Quote' : 'No Quotes'}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Created</div>
            <div className="text-sm font-medium text-foreground">
              {new Date(enquiry?.createdAt)?.toLocaleDateString('en-GB')}
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="space-y-4 pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Trip Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trip Type:</span>
                    <span className="text-foreground">{enquiry?.tripType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Accommodation:</span>
                    <span className="text-foreground">{enquiry?.accommodation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="text-foreground">{enquiry?.duration} days</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Requirements</h4>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {enquiry?.requirements}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-4">
          <Button
            variant="default"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={() => onViewMessages(enquiry?.id)}
          >
            Messages {enquiry?.unreadMessages > 0 && `(${enquiry?.unreadMessages})`}
          </Button>
          
          {enquiry?.status === 'open' && (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => onEditEnquiry(enquiry?.id)}
            >
              Edit
            </Button>
          )}
          
          {enquiry?.status !== 'closed' && enquiry?.status !== 'booked' && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={() => onCloseEnquiry(enquiry?.id)}
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryCard;